import { persistentAtom } from '@nanostores/persistent';
import { atom, computed } from 'nanostores';
import { toast } from 'react-toastify';
import {
  trackVersionHistoryPanelOpened,
  trackVersionHistorySnapshotPreviewedFromChat,
  trackVersionHistorySnapshotPreviewedFromHistoryPanel,
  trackVersionHistoryPreviewExited,
  trackVersionHistoryBookmarkAdded,
  trackVersionHistoryBookmarkRemoved,
  trackVersionHistorySnapshotRenamed,
  trackVersionHistoryRestoreStarted,
  trackVersionHistoryRestoreSucceeded,
  trackVersionHistoryRestoreFailed,
} from '~/lib/analytics/version-history';
import { MSG_RESTORE_PREFIX } from '~/lib/constants';
import { FileSystemAdapter } from '~/lib/snapshots/file-system-adapter';
import { Snapshot } from '~/lib/snapshots/snapshot';
import { snapshotManager } from '~/lib/snapshots/snapshot-manager';
import { chatStore } from '~/lib/stores/chat';
import type { FileMap } from '~/lib/stores/files';
import { projectStore } from '~/lib/stores/project';
import { workbenchStore } from '~/lib/stores/workbench';

class PreviewSessionStore {
  /**
   * The currently previewed version.
   */
  snapshot = atom<Snapshot | null>(null);

  /**
   * The backup files of working draft before previewing a version.
   */
  backupFiles = atom<FileMap | null>(null);

  /**
   * Whether the user has modified any files in the working draft before previewing a version.
   */
  hasUserModifiedFiles = atom<boolean>(false);

  /**
   * Resets the preview session.
   */
  reset() {
    this.snapshot.set(null);
    this.backupFiles.set(null);
    this.hasUserModifiedFiles.set(false);
  }
}

class RestoreConfirmationDialogStore {
  isOpen = atom<boolean>(false);

  snapshot = atom<Snapshot | null>(null);

  isRestoring = atom<boolean>(false);

  open(snapshot: Snapshot) {
    this.snapshot.set(snapshot);
    this.isOpen.set(true);
  }

  close() {
    this.isOpen.set(false);
    this.snapshot.set(null);
  }
}

class VersionHistoryStore {
  fs = new FileSystemAdapter();

  /**
   * Whether the version history drawer is open.
   */
  isOpen = atom<boolean>(false);

  /**
   * Whether the bookmarked versions accordion is open.
   */
  bookmarkedVersionsAccordionState = persistentAtom<'open' | 'closed'>(
    'version-history:bookmarked-versions-accordion',
    'open',
  );

  /**
   * Whether to filter and show only published versions.
   */
  filterPublished = atom<boolean>(false);

  /**
   * Search query for filtering versions.
   */
  searchQuery = atom<string>('');

  /**
   * Restore confirmation dialog state.
   */
  restoreConfirmationDialog = new RestoreConfirmationDialogStore();

  /**
   * Preview session containing the currently previewed version and backup files.
   */
  previewSession = new PreviewSessionStore();

  snapshots = computed([snapshotManager.snapshots], (snapshots) => {
    return Object.values(snapshots).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  });

  /**
   * Computed property for the currently previewed version.
   */
  previewedSnapshot = computed([this.previewSession.snapshot], (snapshot) => snapshot);

  isProcessingFiles = computed(
    [workbenchStore.isProcessing, workbenchStore.waitingForImport],
    (isProcessing, waitingForImport) => {
      return isProcessing || waitingForImport;
    },
  );

  /**
   * Toggles the open state of the version history drawer.
   */
  toggleOpen() {
    const wasOpen = this.isOpen.get();
    this.isOpen.set(!wasOpen);

    if (!wasOpen) {
      trackVersionHistoryPanelOpened();
    }
  }

  canSnapshotBePreviewed(item: Snapshot) {
    return item.id !== this.previewedSnapshot.get()?.id && item.id !== this.snapshots.get()[0]?.id;
  }

  async toggleBookmark(item: Snapshot) {
    const projectId = projectStore.getProjectId();

    if (!projectId) {
      return;
    }

    const wasBookmarked = item.isBookmarked;

    await snapshotManager.toggleBookmark(projectId, item.messageId, !item.isBookmarked);

    if (wasBookmarked) {
      trackVersionHistoryBookmarkRemoved(item.messageId);
    } else {
      trackVersionHistoryBookmarkAdded(item.messageId);
    }
  }

  async updateTitle(item: Snapshot, title: string) {
    const projectId = projectStore.getProjectId();

    if (!projectId) {
      return;
    }

    await snapshotManager.updateTitle(projectId, item.messageId, title);

    trackVersionHistorySnapshotRenamed(item.messageId);
  }

  async previewSnapshot(item: Snapshot, source: 'chat' | 'history-panel' = 'history-panel') {
    if (!this.canSnapshotBePreviewed(item)) {
      const snapshot = this.previewSession.snapshot.get();

      if (snapshot) {
        await this.cancelPreview();
        return;
      }

      return;
    }

    const files = workbenchStore.files.get();
    const backupFiles = this.previewSession.backupFiles.get();

    chatStore.isReadOnly.set(true);

    workbenchStore.currentView.set('preview');

    // save current files in memory if we haven't already
    if (!backupFiles) {
      await workbenchStore.saveAllFiles();

      this.previewSession.snapshot.set(item);
      this.previewSession.backupFiles.set(structuredClone(files));
      this.previewSession.hasUserModifiedFiles.set(workbenchStore.hasUserModifiedFiles);
    } else {
      this.previewSession.snapshot.set(item);
    }

    await snapshotManager.previewSnapshot(item.messageId, this.fs);

    if (source === 'chat') {
      trackVersionHistorySnapshotPreviewedFromChat(item.messageId);
    } else {
      trackVersionHistorySnapshotPreviewedFromHistoryPanel(item.messageId);
    }
  }

  async cancelPreview() {
    const backupFiles = this.previewSession.backupFiles.get();
    const hasUserModifiedFiles = this.previewSession.hasUserModifiedFiles.get();
    const previewedSnapshot = this.previewSession.snapshot.get();

    if (backupFiles) {
      await snapshotManager.restoreFromPreviewSession({ backupFiles, hasUserModifiedFiles }, this.fs);
    }

    this.previewSession.reset();

    chatStore.isReadOnly.set(false);

    workbenchStore.setFileListenerEnabled(true);

    if (previewedSnapshot) {
      trackVersionHistoryPreviewExited(previewedSnapshot.messageId);
    }
  }

  async confirmRestoreSnapshot() {
    try {
      this.restoreConfirmationDialog.isRestoring.set(true);

      const snapshot = this.restoreConfirmationDialog.snapshot.get();

      if (!snapshot) {
        return;
      }

      trackVersionHistoryRestoreStarted(snapshot.messageId);

      await this.cancelPreview();

      await workbenchStore.saveAllFiles();

      const projectId = projectStore.getProjectId();

      if (!projectId) {
        toast.error('Failed to restore snapshot: No project ID found');
        trackVersionHistoryRestoreFailed(snapshot.messageId);

        return;
      }

      try {
        await snapshotManager.restoreSnapshot(snapshot.messageId, this.fs);

        chatStore.events.dispatch('sendChatMessage', {
          message: `${MSG_RESTORE_PREFIX} ${snapshot.displayTitle}`,
          mode: 'build',
          details: {
            targetMessageId: snapshot.messageId,
          },
        });

        trackVersionHistoryRestoreSucceeded(snapshot.messageId);
      } catch (error) {
        toast.error('Failed to restore snapshot');
        trackVersionHistoryRestoreFailed(snapshot.messageId);
        throw error;
      }

      this.restoreConfirmationDialog.close();

      workbenchStore.reloadPreview();
    } catch {
      const snapshot = this.restoreConfirmationDialog.snapshot.get();
      toast.error('Failed to restore snapshot');

      if (snapshot) {
        trackVersionHistoryRestoreFailed(snapshot.messageId);
      }
    } finally {
      this.restoreConfirmationDialog.isRestoring.set(false);
    }
  }
}

export const versionHistoryStore = new VersionHistoryStore();
